import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthReady } from "@/hooks/useAuthReady";

interface NavMeta {
  userName: string;
  isAdmin: boolean;
  isPartner: boolean;
}

/**
 * Caches profile / role / partner data for the Navigation bar.
 * Shared via React Query – survives route changes without re-fetching.
 */
export function useNavMeta() {
  const { user, isReady } = useAuthReady();

  return useQuery<NavMeta>({
    queryKey: ["nav-meta", user?.id],
    enabled: isReady && !!user,
    staleTime: 5 * 60 * 1000, // 5 min
    queryFn: async () => {
      const userId = user!.id;
      const [{ data: profile }, { data: roles }, { data: partnerData }] = await Promise.all([
        supabase.from("profiles").select("full_name").eq("user_id", userId).single(),
        supabase.from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle(),
        (supabase as any).from("partner_enrollments").select("id").eq("user_id", userId).eq("status", "active").maybeSingle(),
      ]);

      const metadata = user!.user_metadata;
      const userName =
        profile?.full_name ||
        metadata?.full_name ||
        metadata?.name ||
        user!.email?.split("@")[0] ||
        "User";

      return {
        userName,
        isAdmin: !!roles,
        isPartner: !!partnerData,
      };
    },
  });
}
